import _ from 'lodash';
import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';
import {
  createCustomSpaceType as createCustomSpaceTypeApi,
  deleteCustomSpaceType as deleteCustomSpaceTypeApi,
  updateCustomSpaceType as updateCustomSpaceTypeApi,
  createChildWorksheetSpaceType as createChildWorksheetSpaceTypeApi,
} from 'spw-api';
import { IAppState } from 'spw-redux/reducer';
import {
  actions as projectActions,
  GET_SPACE_TYPE_CATEGORIES_SUCCEEDED,
} from 'spw-redux/modules/projects/actions';
import {
  handleError,
  showToastMessage,
} from 'spw-redux/modules/UI/messages/actions';
import { APIResponse } from 'spw-redux/types';
import {
  DEFAULT_INDIVIDUAL_NAME,
  defaultRowValues,
  isWorkCategory,
} from 'spw-components/worksheet/utils';
import { PROGRAMMING, SHARED_PRIVATE_OFFICE } from 'spw-constants';
import {
  IProjectSpaceType,
  ISpaceType,
} from 'spw-redux/modules/space_types/types';
import {
  actions as spaceTypeActions,
  fetchProjectSpaceTypes,
  fetchProjectSpaceTypesSuccess,
} from 'spw-redux/modules/space_types/actions';
import { getDefaultSpaceTypeCategory } from 'spw-redux/modules/project-space-type-categories/utils';
import { currentProject } from 'spw-redux/modules/projects/selectors';
import { selectProjectSpaceTypes } from 'spw-redux/modules/space_types/selectors';
import { isCustomSubcategory } from 'spw-projects/utils';
import {
  actions,
  CREATE_CUSTOM_ESA_SPACE_TYPE,
  CREATE_CUSTOM_SPACE_TYPE,
  CREATE_CUSTOM_SPACE_TYPE_CHILD,
  DELETE_CUSTOM_SPACE_TYPE,
  UPDATE_CUSTOM_SPACE_TYPE,
} from './actions';
import { getCurrentProject } from '../projects/sagas';
import { IFloor, IProject } from '../projects/types';
import { getSpaces } from '../floorplan/actions';
import {
  fetchAllChildWorksheetSpaces,
  postWorksheetRow,
} from '../calculators/actions';
import { selectWorksheet } from '../calculators/selectors';
import { PlainWorksheet } from '../../../models/Worksheet';

const getCurrentParent = (state: IAppState) =>
  state.customSpacesReducer.currentParent;

const getCurrentFloor = (state: IAppState) =>
  state.projectsReducer.currentFloor;

function* createESACustomSpaceType(
  action: ReturnType<typeof actions.createCustomESASpaceType>
) {
  try {
    const {
      spaceType,
      parentSpaceType: parent,
      clientFacingName,
    } = action.payload;
    const project: IProject = yield select(getCurrentProject);

    yield call(
      createCustomSpaceTypeApi,
      project.id,
      parent.id,
      spaceType,
      undefined,
      clientFacingName
    );

    yield put(spaceTypeActions.fetchProjectSpaceTypes());
    yield put(projectActions.getSpaceTypeCategories());
  } catch (e) {
    yield put(handleError(e));
  }
}

function* createCustomSpaceType(
  action: ReturnType<typeof actions.createCustomSpaceType>
) {
  try {
    const {
      spaceType,
      clientFacingName,
      previousId,
      insertToWorksheet,
      callback,
    } = action.payload;
    const project: IProject = yield select(getCurrentProject);
    const parent: ISpaceType = yield select(getCurrentParent);

    const response: APIResponse<typeof createCustomSpaceTypeApi> = yield call(
      createCustomSpaceTypeApi,
      project.id,
      parent.id,
      _.omit(spaceType, 'spaceTypeCategory'),
      previousId,
      clientFacingName
    );

    if (response?.data?.createCustomSpaceType && insertToWorksheet) {
      let { createCustomSpaceType: newProjectSpaceType } = response?.data;

      newProjectSpaceType = {
        ...newProjectSpaceType,
        category: {
          ...newProjectSpaceType.category,
          defaultSpaceTypeCategory: getDefaultSpaceTypeCategory(
            newProjectSpaceType.category.defaultSpaceTypeCategory
          ),
        },
      };

      const worksheet: PlainWorksheet = yield select(selectWorksheet);
      let seatCap = 0;

      if (isCustomSubcategory(newProjectSpaceType.category)) {
        seatCap = 1;
      } else if (
        isWorkCategory({ category: spaceType.spaceTypeCategory?.category })
      ) {
        seatCap = spaceType.spaceType === SHARED_PRIVATE_OFFICE ? 2 : 1;
      }

      yield put(
        postWorksheetRow(
          {
            ...defaultRowValues,
            seatCap,
            spaceType: {
              ...spaceType,
              name: DEFAULT_INDIVIDUAL_NAME,
              id: newProjectSpaceType.spaceType.id,
              parent,
              spaceType: parent.spaceType,
            },
            projectSpaceType: {
              ...newProjectSpaceType,
            } as Partial<IProjectSpaceType>,
            category: newProjectSpaceType.category,
          },
          worksheet.id
        )
      );
    }
    yield put(fetchProjectSpaceTypes());
    yield put(projectActions.getSpaceTypeCategories());
    yield put(
      showToastMessage({
        text: 'The space type was successfully created!',
        status: 'success',
      })
    );
    if (callback) callback();
  } catch (e) {
    yield put(handleError(e));
  }
}

function* createCustomSpaceTypeChild(
  action: ReturnType<typeof actions.createCustomSpaceTypeChild>
) {
  try {
    const {
      name,
      spaceTypeId,
      unitsHigh,
      clientFacingName,
      callback,
    } = action.payload;
    const project: IProject = yield select(currentProject);
    yield call(createChildWorksheetSpaceTypeApi, {
      name,
      clientFacingName,
      spaceTypeId,
      projectId: project.id,
      unitsHigh,
    });
    yield put(fetchAllChildWorksheetSpaces());
    yield put(
      showToastMessage({
        text: 'The space type was successfully created!',
        status: 'success',
      })
    );
    if (callback) callback();
  } catch (e) {
    yield put(handleError(e));
  }
}

function* updateCustomSpaceType(
  action: ReturnType<typeof actions.updateCustomSpaceType>
) {
  try {
    const { spaceTypeId, spaceType, clientFacingName } = action.payload;
    const project: IProject = yield select(getCurrentProject);

    yield call(
      updateCustomSpaceTypeApi,
      spaceTypeId,
      spaceType,
      clientFacingName
    );
    yield put(projectActions.getSpaceTypeCategories());

    const floor: IFloor = yield select(getCurrentFloor);
    if (floor) {
      yield put(getSpaces(floor.id));
    }

    yield take(GET_SPACE_TYPE_CATEGORIES_SUCCEEDED);
    yield put(fetchProjectSpaceTypes());

    if (project.type === PROGRAMMING) {
      yield put(fetchAllChildWorksheetSpaces());
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* deleteCustomSpaceType(
  action: ReturnType<typeof actions.deleteCustomSpaceType>
) {
  try {
    const { spaceType } = action.payload;
    const project: IProject = yield select(currentProject);
    const spaceTypes: IProjectSpaceType[] = yield select(
      selectProjectSpaceTypes
    );

    const response: APIResponse<typeof deleteCustomSpaceTypeApi> = yield call(
      deleteCustomSpaceTypeApi,
      project.id,
      spaceType.id
    );

    const deletedSpaceTypeId = response.data.deletedSpaceType.id;

    const newSpaceTypes = spaceTypes
      .slice()
      .filter((st: IProjectSpaceType) => st.id !== deletedSpaceTypeId);

    yield put(projectActions.getSpaceTypeCategories());
    const floor: IFloor = yield select(getCurrentFloor);
    yield put(getSpaces(floor.id));
    yield put(fetchProjectSpaceTypesSuccess(newSpaceTypes));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* saga() {
  yield all([
    takeLatest(CREATE_CUSTOM_SPACE_TYPE, createCustomSpaceType),
    takeLatest(CREATE_CUSTOM_SPACE_TYPE_CHILD, createCustomSpaceTypeChild),
    takeLatest(CREATE_CUSTOM_ESA_SPACE_TYPE, createESACustomSpaceType),
    takeLatest(UPDATE_CUSTOM_SPACE_TYPE, updateCustomSpaceType),
    takeLatest(DELETE_CUSTOM_SPACE_TYPE, deleteCustomSpaceType),
  ]);
}

export default saga;
